/**
 * AriaAccountKnowledge — "Cervello condiviso" del proprietario.
 *
 * URL: /app/aria/account
 *
 * Tutto quello che inserisci qui è ereditato da TUTTE le Aria che possiedi.
 * Es.: brand voice, company info, policies, do_say / dont_say.
 *
 * Si fonde con la Knowledge specifica del singolo widget al momento della chat.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft, Save, Brain, Sparkles, Building2, Shield,
    PackageSearch, MessageSquareQuote, ThumbsUp, ThumbsDown, Check,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SECTIONS = [
    { key: "brand_voice", label: "Brand Voice", icon: MessageSquareQuote, color: "#A855F7",
      desc: "Il tono che TUTTE le tue Aria devono mantenere (es. \"caldo, professionale, mai aggressivo, italiano\").",
      placeholder: "Caldo, professionale, mai aggressivo. Sempre lei (formale). Italiano." },
    { key: "company_info", label: "Company Info", icon: Building2, color: "#4F8BFF",
      desc: "Chi sei: ragione sociale, P.IVA, sede, mission. Aria lo cita quando serve.",
      placeholder: "TotalKlick srls, P.IVA 10559691216, sede Roma. Ecosistema SaaS multi-modulo." },
    { key: "policies", label: "Policy globali", icon: Shield, color: "#10B981",
      desc: "Politiche trasversali: rimborso, privacy aggiuntiva, orari di assistenza, tempi di risposta.",
      placeholder: "Rimborso 14gg. Assistenza Lun-Ven 9-18. Privacy GDPR-compliant, dati conservati 24 mesi." },
    { key: "products_master", label: "Catalogo / Servizi master", icon: PackageSearch, color: "#FFC36B",
      desc: "Catalogo o lista servizi ad alto livello. Le Aria specifiche possono dettagliare di più.",
      placeholder: "5 moduli: TotalPL (PrintLab), TotalKlickADS, Aria Core, OfferteInCittà (EXT), GoStreet (EXT)." },
    { key: "common_faq", label: "FAQ trasversali", icon: Sparkles, color: "#FF1A5C",
      desc: "Domande/risposte valide su QUALSIASI tuo sito (es. dove mi contatti, P.IVA, orari).",
      placeholder: "Q: Come vi contatto? A: Lascia il tuo numero qui o scrivi a hello@totalklick.it.\nQ: Dove avete sede? A: Roma." },
    { key: "do_say", label: "Do say", icon: ThumbsUp, color: "#86EFAC",
      desc: "Cose che Aria DEVE dire (USP, value prop, slogan).",
      placeholder: "Siamo l'unico ecosistema italiano che parla con i clienti 24/7 tramite AI." },
    { key: "dont_say", label: "Don't say", icon: ThumbsDown, color: "#FF7AA3",
      desc: "Cose che Aria NON deve mai dire (promesse impossibili, comparazioni con competitor).",
      placeholder: "Mai promesse di risultati garantiti. Mai paragoni diretti con altri prodotti." },
];

export default function AriaAccountKnowledge() {
    const nav = useNavigate();
    const [form, setForm] = useState(Object.fromEntries(SECTIONS.map((s) => [s.key, ""])));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);
    const [recentSave, setRecentSave] = useState(false);

    useEffect(() => {
        document.title = "Brand & Policies · Aria";
        (async () => {
            try {
                const { data } = await api.get("/widget/account/knowledge");
                const init = {};
                SECTIONS.forEach((s) => { init[s.key] = (data?.[s.key]) || ""; });
                setForm(init);
                setSavedAt(data?.updated_at || null);
                setError("");
            } catch (e) { setError(formatApiError(e)); }
            setLoading(false);
        })();
    }, []);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/widget/account/knowledge", form);
            setRecentSave(true);
            setTimeout(() => setRecentSave(false), 2500);
            // Reload per ottenere updated_at fresco
            const { data: fresh } = await api.get("/widget/account/knowledge");
            setSavedAt(fresh?.updated_at || null);
            setError("");
            return data;
        } catch (e) { setError(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="px-6 py-7 max-w-4xl mx-auto" data-testid="aria-account-knowledge">
            <button type="button" onClick={() => nav("/app/aria/console")}
                className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-5">
                <ArrowLeft size={12} /> Console
            </button>

            <div className="flex items-start gap-4 mb-7">
                <div className="w-12 h-12 rounded-xl grid place-items-center flex-none" style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)" }}>
                    <Brain size={18} color="#fff" />
                </div>
                <div className="flex-1">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 mb-1.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                        Cervello condiviso · Account-level
                    </div>
                    <h1 className="text-white text-[24px]" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>
                        Brand &amp; Policies
                    </h1>
                    <p className="text-white/65 text-[13px] mt-2 max-w-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Tutto ciò che scrivi qui è <strong>ereditato da ogni Aria del tuo account</strong>.
                        Aggiorna una volta sola la voce del brand o le policies, e si propagano automaticamente.
                    </p>
                </div>
            </div>

            {error && <div className="mb-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

            {loading ? (
                <div className="text-white/45 text-[12.5px] text-center py-8">caricamento…</div>
            ) : (
                <div className="space-y-4">
                    {SECTIONS.map((s) => (
                        <div key={s.key} className="rounded-2xl p-5"
                            style={{ background: "rgba(15,18,28,0.5)", border: "1px solid rgba(255,255,255,0.06)" }}
                            data-testid={`section-${s.key}`}>
                            <div className="flex items-center gap-2.5 mb-1">
                                <div className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: `${s.color}22` }}>
                                    <s.icon size={13} style={{ color: s.color }} />
                                </div>
                                <span className="text-white text-[14px]" style={{ fontWeight: 500 }}>{s.label}</span>
                            </div>
                            <div className="text-white/55 text-[11.5px] mb-3 leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                                {s.desc}
                            </div>
                            <textarea value={form[s.key] || ""} onChange={(e) => upd(s.key, e.target.value)}
                                placeholder={s.placeholder} rows={s.key === "products_master" || s.key === "common_faq" ? 5 : 3}
                                className="w-full rounded-xl px-3 py-2.5 text-white text-[13px] outline-none transition resize-y"
                                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)", fontFamily: "Outfit, sans-serif" }}
                                data-testid={`input-${s.key}`} />
                            <div className="text-[10.5px] text-white/40 mt-1.5 text-right" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                                {(form[s.key] || "").length} / 10000
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Save bar sticky */}
            <div className="sticky bottom-3 mt-7 flex items-center justify-between gap-3 p-3 rounded-2xl"
                style={{ background: "rgba(10,12,20,0.95)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" }}>
                <div className="text-[11.5px] text-white/55" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {recentSave && <span className="text-[#86EFAC] inline-flex items-center gap-1.5"><Check size={11} /> salvato</span>}
                    {!recentSave && savedAt && <span>ultimo salvataggio: {new Date(savedAt).toLocaleString("it-IT")}</span>}
                    {!recentSave && !savedAt && <span>nessun salvataggio ancora</span>}
                </div>
                <button type="button" onClick={save} disabled={saving || loading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] text-white disabled:opacity-50"
                    style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500 }}
                    data-testid="btn-save-knowledge">
                    <Save size={12} /> {saving ? "Salvataggio…" : "Salva"}
                </button>
            </div>
        </div>
    );
}
